import {
  portalClientSlugProblem,
  portalSigninLinkParam,
  portalSlugLinkValue,
} from "@ledgerbox/domain"

/**
 * The client this page is for, as the API reads it from `x-portal-link`.
 *
 * Two shapes open the same portal: `/c/<token>` and the readable
 * `/{client-slug}`, which travels as `slug:<client-slug>`. Anything else
 * (the demo, a stray path) names no client.
 */
export const readLinkValue = (
  pathname: string = window.location.pathname
): string | null => {
  const token = pathname.match(/^\/c\/([^/]+)\/?$/)?.[1]
  if (token) {
    try {
      return decodeURIComponent(token)
    } catch {
      return null
    }
  }
  const slug = pathname.match(/^\/([^/]+)\/?$/)?.[1]
  if (!slug || portalClientSlugProblem(slug) !== null) return null
  return portalSlugLinkValue(slug)
}

/** The sign-in token in an address, and the address without it. The path and
    the `#request-…` fragment stay, so the page still opens that request. */
export const splitSigninToken = (
  href: string
): { token: string | null; href: string } => {
  const url = new URL(href)
  const token = url.searchParams.get(portalSigninLinkParam)
  if (token === null) return { token: null, href }
  url.searchParams.delete(portalSigninLinkParam)
  return { token: token || null, href: url.toString() }
}

let taken: { token: string | null } | null = null

/**
 * Take the sign-in token out of the address bar before anything else runs,
 * so it never sits in history, a bookmark or a shared screenshot. Read once
 * per page load: a second call (React's strict mode runs initialisers twice)
 * gets the same token, not the stripped address.
 */
export const takeSigninToken = (): string | null => {
  if (taken) return taken.token
  const { token, href } = splitSigninToken(window.location.href)
  if (href !== window.location.href) {
    window.history.replaceState(window.history.state, "", href)
  }
  taken = { token }
  return token
}

/** The request a `#request-<id>` fragment names, when it is on the page. */
export const requestAnchorTarget = (hash: string): string | null =>
  /^#request-[A-Za-z0-9-]+$/.test(hash) ? hash.slice(1) : null

/** Bring the request from the email into view once the list has drawn. */
export const revealRequestAnchor = (): void => {
  const id = requestAnchorTarget(window.location.hash)
  if (id) document.getElementById(id)?.scrollIntoView({ block: "start" })
}
